import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Link2, MapPin, RefreshCw, Search, ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Spinner } from '../../components/ui/Spinner';
import { Checkbox } from '../../components/ui/checkbox';
import { filterMappingAccounts, mappingStatus, pickTokenHolder } from '../../lib/gbpOnboarding';
import type { GbpAgencyLocation, GbpMappingAccount, GbpTokenHolder } from '../../types/gbpOnboarding';
import {
  assignGbpAccount,
  getGbpMappingAccounts,
  getGbpTokenHolders,
  listGbpAgencyLocations,
  reingestReviewsForAccounts,
  switchGbpAccount,
} from './api';

// GBP agency onboarding v2 — the OPERATOR tool. Authorize once by connecting ONE
// account's GBP (the per-account "Connect Google Business" button = the grant /
// "token holder"), then ASSIGN other accounts to their GBP location by reusing
// that stored grant (assignGoogleBusinessLocation) — no per-assignment browser
// step. Selective by design (one account at a time; never bulk). Assigning each
// location to its OWN profile is what fixes the Google-review mis-attribution.

export function GbpOnboardingPage() {
  const tokenHolders = useQuery({ queryKey: ['gbp-token-holders'], queryFn: getGbpTokenHolders });
  const holders = tokenHolders.data?.tokenHolders ?? [];
  const [tokenHolderId, setTokenHolderId] = useState<string | null>(null);
  const holder = useMemo(() => pickTokenHolder(holders, tokenHolderId), [holders, tokenHolderId]);

  // Accumulate assigned accounts so the operator can re-sync their reviews in one
  // pass (the mis-attribution fix — additive/safe).
  const [assigned, setAssigned] = useState<string[]>([]);

  return (
    <div>
      <PageHeader
        title="Google Business (agency)"
        description="Reuse one connected Google Business account's grant to assign other accounts to their location — no re-login per account."
        backHref="/onboarding"
      />
      <TokenHolderCard
        loading={tokenHolders.isLoading}
        holders={holders}
        selected={holder}
        onSelect={setTokenHolderId}
      />
      {holder && (
        <MappingSection
          tokenHolder={holder}
          assigned={assigned}
          onAssigned={(accountId) => setAssigned((cur) => (cur.includes(accountId) ? cur : [...cur, accountId]))}
        />
      )}
    </div>
  );
}

function TokenHolderCard({
  loading,
  holders,
  selected,
  onSelect,
}: {
  loading: boolean;
  holders: GbpTokenHolder[];
  selected: GbpTokenHolder | null;
  onSelect: (id: string) => void;
}) {
  if (loading) {
    return (
      <div className="mb-6 flex items-center gap-2 rounded-lg border border-slate-200 p-4 text-sm text-slate-500">
        <Spinner className="h-4 w-4" /> Checking the agency grant…
      </div>
    );
  }

  if (holders.length === 0) {
    return (
      <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
        <p className="text-sm font-semibold text-amber-900">Step 1 — authorize the agency grant</p>
        <p className="mt-1 text-sm text-amber-800">
          Connect ONE account's Google Business first — use the “Connect Google Business” button on
          that account's API Connections section, signed into the agency Google account (open it in
          an incognito window if the chooser doesn't appear). That stored grant is then reused here to
          assign every other account without re-logging in.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-6 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
      <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
      <div className="flex-1 text-sm text-emerald-900">
        <p className="font-semibold">Assigning through the agency grant</p>
        <p className="mt-0.5">
          Locations are assigned by reusing this connected account's Google Business grant — no
          per-account login.
        </p>
        <label className="mt-2 flex items-center gap-2 text-sm">
          <span className="text-emerald-800">Grant source:</span>
          <select
            value={selected?.accountId ?? ''}
            className="h-8 rounded-md border border-emerald-300 bg-white px-2 text-sm text-slate-900"
            onChange={(e) => onSelect(e.target.value)}
          >
            {holders.map((h) => (
              <option key={h.accountId} value={h.accountId}>
                {/* Headline = the connected Google login (the reusable agency
                    grant), not the kyck location. The kyck account name is a
                    secondary disambiguator (omitted when it duplicates). */}
                {h.googleAccountName}
                {h.name && h.name !== h.googleAccountName ? ` · ${h.name}` : ''}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}

function MappingSection({
  tokenHolder,
  assigned,
  onAssigned,
}: {
  tokenHolder: GbpTokenHolder;
  assigned: string[];
  onAssigned: (accountId: string) => void;
}) {
  const queryClient = useQueryClient();
  const accounts = useQuery({ queryKey: ['gbp-agency-accounts'], queryFn: getGbpMappingAccounts });
  const [search, setSearch] = useState('');
  const [onlyConnectable, setOnlyConnectable] = useState(true);
  const [hideConnected, setHideConnected] = useState(false);
  const [openRowId, setOpenRowId] = useState<string | null>(null);

  const filtered = useMemo(
    () => filterMappingAccounts(accounts.data?.accounts ?? [], { search, onlyConnectable, hideConnected }),
    [accounts.data, search, onlyConnectable, hideConnected],
  );
  const shown = filtered.slice(0, 200);

  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of accounts.data?.accounts ?? []) m.set(a.id, a.name);
    return m;
  }, [accounts.data]);

  const reingest = useMutation({
    mutationFn: () => reingestReviewsForAccounts(assigned),
    onSuccess: () => toast.success('Re-synced reviews for the assigned accounts — they now resolve to the correct account.'),
    onError: (err: Error) => toast.error(err.message),
  });

  const afterAssign = (accountId: string) => {
    setOpenRowId(null);
    onAssigned(accountId);
    void queryClient.invalidateQueries({ queryKey: ['gbp-agency-accounts'] });
  };

  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-slate-900">Step 2 — assign specific accounts</p>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative min-w-64 flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search accounts by name or type…"
            className="pl-8"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <Checkbox checked={onlyConnectable} onCheckedChange={(c) => setOnlyConnectable(c === true)} />
          Assignable only
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <Checkbox checked={hideConnected} onCheckedChange={(c) => setHideConnected(c === true)} />
          Hide connected
        </label>
      </div>

      {assigned.length > 0 && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
          <span className="text-slate-600">
            Assigned {assigned.length} account{assigned.length === 1 ? '' : 's'} this session:{' '}
            {assigned.map((id) => nameById.get(id) ?? id).join(', ')}
          </span>
          <Button size="sm" variant="outline" onClick={() => reingest.mutate()} disabled={reingest.isPending}>
            {reingest.isPending ? <Spinner className="h-4 w-4" /> : <RefreshCw className="h-4 w-4" />}
            Re-sync reviews
          </Button>
        </div>
      )}

      {accounts.isLoading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
          <Spinner className="h-4 w-4" /> Loading accounts…
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200">
          {shown.map((account) => (
            <AccountRow
              key={account.id}
              account={account}
              tokenHolder={tokenHolder}
              open={openRowId === account.id}
              onToggle={() => setOpenRowId((id) => (id === account.id ? null : account.id))}
              onAssigned={afterAssign}
            />
          ))}
          {shown.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-slate-500">
              No accounts match the current filters.
            </p>
          )}
          {filtered.length > shown.length && (
            <p className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-xs text-slate-500">
              Showing the first {shown.length} of {filtered.length}. Narrow the search to find a
              specific account.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

const STATUS_BADGE: Record<ReturnType<typeof mappingStatus>, { label: string; className: string }> = {
  connected: { label: 'Connected', className: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  'needs-profile': { label: 'No Zernio profile', className: 'border-slate-200 bg-slate-50 text-slate-500' },
  'not-connected': { label: 'Not connected', className: 'border-amber-200 bg-amber-50 text-amber-700' },
};

function AccountRow({
  account,
  tokenHolder,
  open,
  onToggle,
  onAssigned,
}: {
  account: GbpMappingAccount;
  tokenHolder: GbpTokenHolder;
  open: boolean;
  onToggle: () => void;
  onAssigned: (accountId: string) => void;
}) {
  const status = mappingStatus(account);
  const badge = STATUS_BADGE[status];
  const assignable = status === 'not-connected';
  const connected = status === 'connected';

  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <div className="flex items-center gap-3 px-4 py-2.5">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-900">{account.name}</p>
          <p className="text-xs text-slate-400">{account.accountType}</p>
        </div>
        <Badge variant="outline" className={badge.className}>
          {badge.label}
        </Badge>
        {assignable ? (
          <Button size="sm" variant={open ? 'secondary' : 'outline'} onClick={onToggle}>
            {open ? 'Cancel' : 'Assign location'}
          </Button>
        ) : connected ? (
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            <Button size="sm" variant="ghost" onClick={onToggle}>
              {open ? 'Cancel' : 'Switch'}
            </Button>
          </div>
        ) : (
          <span className="text-xs text-slate-400" title="Connect Meta via onboarding first">
            Needs Meta first
          </span>
        )}
      </div>
      {open && (assignable || connected) && (
        <LocationPicker
          account={account}
          tokenHolder={tokenHolder}
          mode={connected ? 'switch' : 'assign'}
          onAssigned={onAssigned}
        />
      )}
    </div>
  );
}

function LocationPicker({
  account,
  tokenHolder,
  mode,
  onAssigned,
}: {
  account: GbpMappingAccount;
  tokenHolder: GbpTokenHolder;
  mode: 'assign' | 'switch';
  onAssigned: (accountId: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState<GbpAgencyLocation | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(handle);
  }, [search]);

  const locations = useQuery({
    queryKey: ['gbp-agency-locations', tokenHolder.accountId, debounced],
    queryFn: () => listGbpAgencyLocations(tokenHolder.accountId, debounced || undefined),
  });

  const doAssign = useMutation({
    mutationFn: async (location: GbpAgencyLocation) => {
      const payload = {
        locationId: location.id,
        googleAccountId: location.googleAccountId ?? undefined,
        locationName: location.name,
      };
      if (mode === 'switch') {
        return switchGbpAccount({ targetAccountId: account.id, ...payload });
      }
      const res = await assignGbpAccount({
        tokenHolderAccountId: tokenHolder.accountId,
        targetAccountId: account.id,
        ...payload,
      });
      // 409 — the target already has a GBP connection; offer the switch.
      if (res.status === 'needs_switch') {
        if (
          window.confirm(
            `"${account.name}" already has a Google Business connection. Switch it to "${location.name}" instead?`,
          )
        ) {
          return switchGbpAccount({ targetAccountId: account.id, ...payload });
        }
        return res;
      }
      return res;
    },
    onSuccess: (res) => {
      if (res.status === 'assigned' || res.status === 'switched') {
        toast.success(
          `${res.status === 'switched' ? 'Switched' : 'Assigned'} ${account.name} → ${res.locationName ?? 'location'}.`,
        );
        onAssigned(account.id);
      } else if (res.status === 'error' && res.error) {
        toast.error(res.error);
      }
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const onConfirm = () => {
    if (!selected) return;
    const verb = mode === 'switch' ? 'Switch to' : 'Assign';
    if (
      window.confirm(
        `${verb} the Google Business location "${selected.name}" for "${account.name}"? This links reviews for this account.`,
      )
    ) {
      doAssign.mutate(selected);
    }
  };

  return (
    <div className="border-t border-slate-100 bg-slate-50/70 px-4 py-3">
      <div className="relative mb-2">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search the agency's Google Business locations…"
          className="pl-8"
          autoFocus
        />
      </div>
      {locations.isLoading ? (
        <div className="flex items-center gap-2 py-3 text-sm text-slate-500">
          <Spinner className="h-4 w-4" /> Loading locations…
        </div>
      ) : locations.isError ? (
        <p className="py-2 text-sm text-rose-600">
          {(locations.error as Error).message ||
            'Could not load locations — the agency grant may have expired. Reconnect the grant source account.'}
        </p>
      ) : (
        <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white">
          {(locations.data?.locations ?? []).map((location) => (
            <button
              key={location.id}
              type="button"
              onClick={() => setSelected(location)}
              className={
                selected?.id === location.id
                  ? 'block w-full border-b border-slate-100 bg-kyck-50 px-3 py-2 text-left'
                  : 'block w-full border-b border-slate-100 px-3 py-2 text-left hover:bg-slate-50'
              }
            >
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">
                  {location.name}
                </span>
                {location.storeCode && (
                  <span className="text-xs text-slate-400">#{location.storeCode}</span>
                )}
              </div>
              {(location.address || location.category) && (
                <p className="ml-6 mt-0.5 truncate text-xs text-slate-500">
                  {[location.address, location.category].filter(Boolean).join(' · ')}
                </p>
              )}
            </button>
          ))}
          {(locations.data?.locations ?? []).length === 0 && (
            <p className="px-3 py-4 text-center text-sm text-slate-500">
              No locations found{debounced ? ` for "${debounced}"` : ''}.
            </p>
          )}
          {locations.data?.hasMore && (
            <p className="border-t border-slate-100 bg-slate-50 px-3 py-1.5 text-xs text-slate-500">
              More locations exist — narrow the search.
            </p>
          )}
        </div>
      )}
      <div className="mt-2 flex items-center justify-end gap-2">
        {selected && (
          <button
            type="button"
            onClick={() => setSelected(null)}
            className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}
        <Button size="sm" onClick={onConfirm} disabled={!selected || doAssign.isPending}>
          {doAssign.isPending ? <Spinner className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
          {mode === 'switch' ? 'Switch to' : 'Assign'} {selected ? `"${selected.name}"` : 'location'}
        </Button>
      </div>
    </div>
  );
}
